import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { StorageHelper } from 'src/app/_utilities/storage-helper';

/**
 * A Moderator (an approver holding a Moderator role configured for the business) has no general
 * Content Studio access: their work happens in MPC's approval management. So when such a user
 * lands anywhere in Content Studio, the app sends them to the full-page "Hang Tight!" screen
 * (ApprovalRedirectComponent), which forwards them to MPC.
 *
 * The one exception is the article editor: approval management links a Moderator to
 * /manage-content/articles/create-detailed-post/edit/:ArticleId to review the content, and that
 * route opens normally.
 *
 * Moderation access is resolved server-side at session bootstrap and arrives on the user object
 * (see ModerationService on the backend).
 */
@Injectable({
  providedIn: 'root'
})
export class ModeratorRedirectService {
  /** Route of the interstitial screen. Must stay outside AuthGuard so it cannot loop. */
  static readonly REDIRECT_ROUTE = '/approval-redirect';

  /** MPC path a Moderator is forwarded to. */
  private static readonly APPROVAL_MANAGEMENT_PATH = 'workflow/approval-management';

  /** The only route a Moderator may open: the editor of an existing article. */
  private static readonly EDITOR_PATH_PATTERN = /^\/manage-content\/articles\/create-detailed-post\/edit\/[^/?#]+\/?$/;

  private readonly _storageHelper: StorageHelper;

  constructor(private router: Router) {
    this._storageHelper = new StorageHelper();
  }

  /** The MPC approval management URL, or null when there is no MPC base url to build it from. */
  get approvalManagementUrl(): string | null {
    const returnUrl = this._storageHelper.user?.returnUrl;
    if (!returnUrl) {
      return null;
    }
    return `${returnUrl.replace(/\/+$/, '')}/${ModeratorRedirectService.APPROVAL_MANAGEMENT_PATH}`;
  }

  /**
   * Where a Moderator should be sent instead of `targetUrl`, or null when the navigation may go
   * ahead: the user is not a Moderator, the target is the article editor, or the user is a
   * Moderator with no MPC url to go to — in which case the app opens as usual rather than
   * leaving them on a dead end.
   */
  moderatorRedirect(targetUrl: string): UrlTree | null {
    if (!this._storageHelper.isModerator || this.isEditorUrl(targetUrl)) {
      return null;
    }
    if (!this.approvalManagementUrl) {
      console.error('Moderator has no MPC return url; skipping the approval management redirect.');
      return null;
    }
    return this.router.createUrlTree([ModeratorRedirectService.REDIRECT_ROUTE]);
  }

  private isEditorUrl(url: string): boolean {
    const path = (url || '').split(/[?#]/)[0];
    return ModeratorRedirectService.EDITOR_PATH_PATTERN.test(path);
  }
}
